/**
 * Setări (App-Setari.dc.html) — tab-uri: Profil (afacere) / Notificări / Facturare
 * (Firmă-PF + istoric plăți) / Cont (securitate, sesiuni, 2FA, passkey, Aspect: temă,
 * GDPR: export + ștergere definitivă).
 */
import { useEffect, useState } from 'react';
import { useBillingProfiles, useBusinessProfile, useMe, useOrders, useSessions, useMutation, useInvalidate, qk } from '../api/hooks';
import { gdprDeleteRequest, gdprExport, revokeSession, updateBusinessProfile } from '../api/endpoints';
import { humanError } from '../api/client';
import { useSession } from '../context/SessionContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../lib/theme';
import type { ThemePref } from '../lib/theme';
import { BillingProfiles } from '../components/BillingProfiles';
import { Modal, PageHeader, Segmented, Skeleton, Spinner } from '../components/ui';
import { SearchSelect } from '../components/SearchSelect';
import { formatDateTime, formatRon } from '../lib/format';

type Tab = 'profil' | 'notificari' | 'facturare' | 'cont';

export function SetariPage() {
  const [tab, setTab] = useState<Tab>('profil');
  useEffect(() => {
    document.title = 'Setări — Reclame Video';
  }, []);

  return (
    <div>
      <PageHeader title="Setări" subtitle="Profilul afacerii, notificări, date de facturare și securitatea contului." />
      <div style={{ marginBottom: 20 }}>
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'profil', label: 'Profil afacere' },
            { value: 'notificari', label: 'Notificări' },
            { value: 'facturare', label: 'Facturare' },
            { value: 'cont', label: 'Cont' },
          ]}
        />
      </div>
      {tab === 'profil' && <ProfileTab />}
      {tab === 'notificari' && <NotificationsTab />}
      {tab === 'facturare' && <BillingTab />}
      {tab === 'cont' && <AccountTab />}
    </div>
  );
}

function ProfileTab() {
  const bp = useBusinessProfile();
  const invalidate = useInvalidate();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', siteUrl: '', description: '', tone: 'prietenos' });

  useEffect(() => {
    if (bp.data) setForm({ name: bp.data.name ?? '', siteUrl: bp.data.siteUrl ?? '', description: bp.data.description ?? '', tone: bp.data.tone ?? 'prietenos' });
  }, [bp.data]);

  const save = useMutation({
    mutationFn: () => updateBusinessProfile({ name: form.name || undefined, siteUrl: form.siteUrl || undefined, description: form.description || undefined, tone: form.tone as never }),
    onSuccess: () => {
      invalidate([qk.business]);
      toast.push({ kind: 'ok', title: 'Profil salvat' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  if (bp.isLoading) return <Skeleton height={220} />;
  return (
    <div className="card" style={{ padding: 20, maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>Aceste date intră automat în scenariile AI.</p>
      <label className="label">Numele afacerii<input className="input" data-testid="setari-biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
      <label className="label">Site<input className="input" value={form.siteUrl} onChange={(e) => setForm({ ...form, siteUrl: e.target.value })} /></label>
      <label className="label">Descriere<textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
      <label className="label">Ton
        <SearchSelect
          value={form.tone}
          onChange={(v) => setForm({ ...form, tone: v })}
          options={[
            { value: 'prietenos', label: 'Prietenos' },
            { value: 'profesional', label: 'Profesional' },
            { value: 'energic', label: 'Energic' },
            { value: 'premium', label: 'Premium' },
          ]}
          aria-label="Ton"
          data-testid="setari-tone"
        />
      </label>
      <button className="btn btn-primary" data-testid="save-profile" onClick={() => save.mutate()} disabled={save.isPending} style={{ alignSelf: 'flex-start' }}>
        {save.isPending ? <Spinner /> : 'Salvează'}
      </button>
    </div>
  );
}

function NotificationsTab() {
  const [prefs, setPrefs] = useState({ email_gata: true, email_livrat: true, email_promo: false, inapp: true });
  const toast = useToast();
  return (
    <div className="card" style={{ padding: 20, maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>Alege ce anunțuri primești pe email. Notificările in-app rămân mereu active.</p>
      {[
        { key: 'email_gata', label: 'Email când un clip e gata de aprobare' },
        { key: 'email_livrat', label: 'Email când un clip e livrat' },
        { key: 'email_promo', label: 'Email cu reduceri și noutăți' },
      ].map((p) => (
        <label key={p.key} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={(prefs as never)[p.key]} onChange={(e) => setPrefs({ ...prefs, [p.key]: e.target.checked })} style={{ accentColor: 'var(--accent)' }} />
          {p.label}
        </label>
      ))}
      <button className="btn btn-primary" onClick={() => toast.push({ kind: 'ok', title: 'Preferințe salvate' })} style={{ alignSelf: 'flex-start' }}>Salvează</button>
    </div>
  );
}

function BillingTab() {
  const billing = useBillingProfiles();
  const orders = useOrders();
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 1fr', gap: 20, alignItems: 'start' }} className="dash-grid">
      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Date de facturare</h2>
        <BillingProfiles profiles={billing.data?.items ?? []} selectedId={selected} onSelect={setSelected} />
      </div>
      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Istoric plăți</h2>
        {orders.isLoading ? (
          <Skeleton height={100} />
        ) : (orders.data?.items ?? []).length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>Nicio comandă încă.</p>
        ) : (
          <div data-testid="order-history" style={{ display: 'flex', flexDirection: 'column' }}>
            {(orders.data?.items ?? []).map((o) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 13.5 }}>{o.orderNumber} · {o.clips} clipuri</p>
                  <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{formatDateTime(o.createdAt)}</span>
                </div>
                <span className="pill" style={{ background: o.status === 'paid' ? 'var(--ok-soft)' : 'var(--bg2)', color: o.status === 'paid' ? 'var(--ok)' : 'var(--muted)' }}>
                  {o.status === 'paid' ? 'Plătit' : o.status === 'pending' ? 'În așteptare' : o.status}
                </span>
                <span className="tnum" style={{ fontWeight: 800, fontSize: 13.5 }}>{formatRon(o.totalCents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountTab() {
  const { user } = useSession();
  const me = useMe();
  const sessions = useSessions();
  const { pref, setPref } = useTheme();
  const invalidate = useInvalidate();
  const toast = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');

  const revoke = useMutation({
    mutationFn: (sid: string) => revokeSession(sid),
    onSuccess: () => {
      invalidate([qk.sessions]);
      toast.push({ kind: 'info', title: 'Sesiune revocată' });
    },
  });
  const exportMut = useMutation({
    mutationFn: () => gdprExport(),
    onSuccess: () => toast.push({ kind: 'ok', title: 'Export declanșat', body: 'Îți trimitem arhiva pe email când e gata.' }),
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  const deleteMut = useMutation({
    mutationFn: () => gdprDeleteRequest(confirmName.trim()),
    onSuccess: () => {
      setDeleteOpen(false);
      toast.push({ kind: 'warn', title: 'Ștergere programată', body: 'Contul va fi șters definitiv după perioada de grație.' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640 }}>
      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Securitate</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0' }}>
          <span style={{ color: 'var(--muted)' }}>Email</span>
          <span style={{ fontWeight: 700 }}>{user.email}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
          <span style={{ color: 'var(--muted)' }}>Autentificare în doi pași (2FA)</span>
          <span className="pill" style={{ background: me.data?.twoFactorEnabled ? 'var(--ok-soft)' : 'var(--bg2)', color: me.data?.twoFactorEnabled ? 'var(--ok)' : 'var(--muted)' }}>
            {me.data?.twoFactorEnabled ? 'Activat' : 'Dezactivat'}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, padding: '8px 0', borderTop: '1px solid var(--line)' }}>
          <span style={{ color: 'var(--muted)' }}>Passkey (amprentă / Face ID)</span>
          <button className="btn btn-ghost btn-sm">Adaugă passkey</button>
        </div>
      </div>

      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Sesiuni active</h2>
        {sessions.isLoading ? (
          <Skeleton height={80} />
        ) : (
          <div data-testid="sessions-list">
            {(sessions.data ?? []).map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--line)' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>{s.userAgent?.slice(0, 48) || 'Dispozitiv necunoscut'} {s.current && <span className="pill" style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}>curent</span>}</p>
                  <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{s.ipAddress || '—'} · creat {formatDateTime(s.createdAt)}</span>
                </div>
                {!s.current && <button className="btn btn-ghost btn-sm" onClick={() => revoke.mutate(s.id)} style={{ color: 'var(--err)' }}>Revocă</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Aspect</h2>
        <Segmented<ThemePref>
          value={pref}
          onChange={setPref}
          options={[
            { value: 'light', label: 'Luminoasă' },
            { value: 'dark', label: 'Întunecată' },
            { value: 'system', label: 'Ca sistemul' },
          ]}
        />
      </div>

      <div className="card" style={{ padding: 20 }}>
        <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>Datele tale (GDPR)</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" data-testid="gdpr-export" onClick={() => exportMut.mutate()} disabled={exportMut.isPending}>
            {exportMut.isPending ? <Spinner /> : 'Exportă arhiva contului'}
          </button>
          <button className="btn btn-ghost btn-sm" data-testid="gdpr-delete" onClick={() => setDeleteOpen(true)} style={{ color: 'var(--err)' }}>
            Șterge contul definitiv
          </button>
        </div>
      </div>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Ștergere definitivă">
        <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--muted)' }}>
          Ștergerea e definitivă, cu o perioadă de grație. Clipurile, fișierele și soldul se pierd. Scrie numele tău ca să confirmi.
        </p>
        <input className="input" data-testid="delete-confirm" placeholder="Numele tău" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} style={{ marginBottom: 12 }} />
        <button className="btn btn-primary" data-testid="delete-submit" style={{ background: 'var(--err)', width: '100%' }} onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending || confirmName.trim().length < 1}>
          {deleteMut.isPending ? <Spinner /> : 'Confirmă ștergerea'}
        </button>
      </Modal>
    </div>
  );
}
